import { ProfilePicture, Loader } from '@getro/rombo';
import { Send, Trash, UserCog } from 'lucide-react';
import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Flex, Text } from 'rebass/styled-components';
import { useHasGoogleSyncFeature } from 'hooks/useHasGoogleSyncFeature';
import { useHasOutlookSyncFeature } from 'hooks/useHasOutlookSyncFeature';
import { useTierGating } from 'hooks/useTierGating';
import { activeNetworkSelector, userSelector } from 'redux/selectors';
import AdminDataTableActionItem from '../AdminDataTableActionItem';
import ContactsSharedCell from '../components/ContactsSharedCell';
import IntegrationsCell from '../components/IntegrationsCell';

export const useAdminData = ({
  admins = [],
  onRemoveAdmin,
  onResendInvitation,
  onUpdateRole,
  isResendingInvitation,
  userToResendInvitation,
  hasActiveFilters = false,
}) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const currentUser = useSelector(userSelector);
  const currentUserId = currentUser?.id;
  const canManageAdmins = !!activeNetwork?.isManager;
  const { isCustomer } = useTierGating({ network: activeNetwork });
  const hasGoogleSync = useHasGoogleSyncFeature({ network: activeNetwork });
  const hasOutlookSync = useHasOutlookSyncFeature({ network: activeNetwork });
  const networkSlug = activeNetwork?.slug || '';
  const menuRef = useRef();
  const teamMemberCount = admins.length;
  const teamMembersHeader = hasActiveFilters
    ? `${teamMemberCount} result${teamMemberCount === 1 ? '' : 's'}`
    : `${teamMemberCount} team member${teamMemberCount === 1 ? '' : 's'}`;
  const columns = [
    {
      Header: teamMembersHeader,
      accessor: 'name',
      width: isCustomer ? '40%' : '90%',
      disableSortBy: true,
      show: true,
    },
    ...(isCustomer
      ? [
          {
            Header: 'Role',
            accessor: 'role',
            width: '15%',
            disableSortBy: true,
            show: true,
          },
          {
            Header: 'Two-factor auth',
            accessor: 'mfaConfigured',
            width: '15%',
            disableSortBy: true,
            show: true,
          },
          {
            Header: 'Contacts shared',
            accessor: 'contactsShared',
            width: '15%',
            disableSortBy: true,
            show: true,
          },
          {
            Header: 'Integrations',
            accessor: 'integrations',
            width: '10%',
            disableSortBy: true,
            show: true,
          },
        ]
      : []),
    {
      Header: '',
      accessor: 'actions',
      width: '10%',
      disableSortBy: true,
      show: canManageAdmins,
    },
  ];

  const actions = useMemo(
    () => [
      {
        title: 'Resend invite',
        icon: Send,
        onClick: (admin) => onResendInvitation(admin),
        show: (admin) => canManageAdmins && !admin.onboardingCompleted && !!onResendInvitation,
      },
      {
        title: 'Edit details',
        icon: UserCog,
        onClick: (e) => onUpdateRole(e, 'edit-role'),
        show: () => canManageAdmins && isCustomer && !!onUpdateRole,
      },
      {
        title: 'Remove',
        icon: Trash,
        onClick: (e) => onRemoveAdmin(e, 'remove'),
        show: () => canManageAdmins && !!onRemoveAdmin,
        sx: {
          color: 'red.600',
        },
      },
    ],
    [canManageAdmins, isCustomer, onRemoveAdmin, onResendInvitation, onUpdateRole],
  );

  const rows = useMemo(
    () =>
      admins.map((admin) => ({
        id: admin.id,
        name: (
          <Flex sx={{ gap: '8px' }} alignItems="flex-start" justifyContent="flex-start" pl="8px">
            <Box width="40px" height="40px" minWidth="40px">
              <ProfilePicture width="40px" height="40px" imageUrl={admin.avatarUrl} name={admin.name} />
            </Box>
            <Box>
              <Text
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                fontSize="14px"
                fontHeight="1.4"
                color="text.dark"
                fontWeight="500"
              >
                {admin.name}
              </Text>
              <Text
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                fontSize="14px"
                fontHeight="1.4"
                color="text.subtle"
                fontWeight="400"
              >
                {admin.email}
              </Text>
              {!admin.onboardingCompleted && (
                <Flex sx={{ gap: '8px', alignItems: 'center', mt: '4px' }}>
                  <Box
                    as="span"
                    sx={{
                      bg: 'orange.50',
                      color: 'orange.600',
                      borderRadius: '4px',
                      px: '6px',
                      py: '1px',
                      fontSize: '12px',
                      fontWeight: '500',
                      lineHeight: '1.4',
                    }}
                  >
                    Pending
                  </Box>
                  {canManageAdmins &&
                    !!onResendInvitation &&
                    (!isResendingInvitation || userToResendInvitation?.id !== admin.id) && (
                      <Box
                        as="a"
                        color="text.subtle"
                        fontSize="14px"
                        sx={{ textDecoration: 'underline', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                        onClick={() => onResendInvitation(admin)}
                      >
                        Resend invite
                      </Box>
                    )}
                  {isResendingInvitation && userToResendInvitation?.id === admin.id && (
                    <Box as="span" display="inline-flex" alignItems="center">
                      <Loader size="small" />
                    </Box>
                  )}
                </Flex>
              )}
            </Box>
          </Flex>
        ),

        role: (
          <Flex
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              justifyContent: 'flex-start',
              width: '100%',
            }}
            fontSize="14px"
            color="text.main"
          >
            {admin.role === 'ally' ? 'Ally' : 'Admin'}
          </Flex>
        ),
        mfaConfigured: (
          <Flex sx={{ gap: '8px', alignItems: 'center', width: '100%' }} fontSize="14px">
            <Box
              width="8px"
              height="8px"
              sx={{ borderRadius: '100%' }}
              backgroundColor={admin.mfaConfigured ? 'icon.success' : 'neutral.300'}
            />
            <Text color="text.main">{admin.mfaConfigured ? 'Enabled' : 'Not enabled'}</Text>
          </Flex>
        ),
        contactsShared: <ContactsSharedCell admin={admin} networkSlug={networkSlug} />,
        integrations: (
          <IntegrationsCell
            admin={admin}
            hasGoogleSync={hasGoogleSync}
            hasOutlookSync={hasOutlookSync}
            networkSlug={networkSlug}
            isCurrentUser={currentUserId === admin.id}
          />
        ),
        actions: (
          <AdminDataTableActionItem
            ref={menuRef}
            actions={actions
              .filter((item) => item.show?.(admin))
              .map((action, index) => ({
                ...action,
                id: index,
                title: typeof action.title === 'function' ? action.title(admin) : action.title,
                onClick: () => action.onClick(admin),
              }))}
            tableItem={admin}
          />
        ),
      })),
    [
      actions,
      admins,
      canManageAdmins,
      currentUserId,
      onResendInvitation,
      isResendingInvitation,
      userToResendInvitation,
      hasGoogleSync,
      hasOutlookSync,
      networkSlug,
    ],
  );

  return {
    columns: columns.filter((column) => column.show),
    rows,
    actions,
  };
};
